import { useCallback, useMemo } from 'react';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES } from 'utils/constants';
import {
  Permission,
  CreatePermissionRequest,
  UpdatePermissionRequest,
} from 'types/permission';

/**
 * Permission API service
 */
export function usePermissionService() {
  const apiClient = useApiClient();

  /**
   * Get all permissions
   */
  const getAllPermissions = useCallback(async (): Promise<Permission[]> => {
    return apiClient.get<Permission[]>(API_ROUTES.PERMISSION);
  }, [apiClient]);

  /**
   * Get a single permission by ID
   */
  const getPermissionById = useCallback(
    async (id: string): Promise<Permission> => {
      return apiClient.get<Permission>(`${API_ROUTES.PERMISSION}/${id}`);
    },
    [apiClient],
  );

  /**
   * Create a new permission
   */
  const createPermission = useCallback(
    async (permission: CreatePermissionRequest): Promise<Permission> => {
      return apiClient.post<Permission>(
        API_ROUTES.PERMISSION,
        permission as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Update a permission
   */
  const updatePermission = useCallback(
    async (
      id: string,
      permission: UpdatePermissionRequest,
    ): Promise<Permission> => {
      return apiClient.patch<Permission>(
        `${API_ROUTES.PERMISSION}/${id}`,
        permission as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Delete a permission
   */
  const deletePermission = useCallback(
    async (id: string): Promise<void> => {
      await apiClient.delete(`${API_ROUTES.PERMISSION}/${id}`);
    },
    [apiClient],
  );

  return useMemo(
    () => ({
      getAllPermissions,
      getPermissionById,
      createPermission,
      updatePermission,
      deletePermission,
    }),
    [
      getAllPermissions,
      getPermissionById,
      createPermission,
      updatePermission,
      deletePermission,
    ],
  );
}
